import { useEffect, useMemo } from "react";
import { useNodes } from "reactflow";
import { useLineageContext, useLineageDispatch } from "../context/LineageProvider";
import { setAllTablesExpanded } from "../context/LineageSlice";

/**
 * When the relationships overlay is enabled (master toggle ON and the user
 * is authenticated), every visible table on the canvas auto-expands its
 * column rows. Turning the overlay off collapses them again.
 *
 * Lives inside the `ReactFlowProvider` so it can call `useNodes()` — that's
 * the reactive subscription to the canvas's table set. Without it, tables
 * added after initial mount (e.g. from "+" expansion) wouldn't auto-open.
 *
 * The previous chevron + popover bulk-action UX has been removed; this
 * component is the only consumer of `setAllTablesExpanded` now.
 */
export const AutoExpandTablesForRefs = () => {
  const dispatch = useLineageDispatch();
  const {
    state: { showRefs, aiEnabled },
  } = useLineageContext();
  const nodes = useNodes();

  // Stable signal — only triggers the effect when the SET of table IDs
  // changes, not on every drag/position update.
  const tableIdsKey = useMemo(
    () =>
      nodes
        .filter(n => n.type === "table")
        .map(n => n.id)
        .sort()
        .join(","),
    [nodes]
  );

  useEffect(() => {
    const ids = tableIdsKey.split(",").filter(Boolean);
    if (ids.length === 0) return;
    dispatch(
      setAllTablesExpanded({
        tables: ids,
        expanded: Boolean(showRefs && aiEnabled),
      })
    );
  }, [showRefs, aiEnabled, tableIdsKey, dispatch]);

  return null;
};
